const _ = require("lodash");
const moment = require("moment");
const LoadState = require("./util/LoadState");
const {getIn, setIn, extendIn} = require("../../../shared/ImmHelpers");
// Must used PageShared instead of OpsPage to prevent cyclic dependency
const PageShared = require("../../../shared/PageShared");
const TrainerCore = require("../../../shared/TrainerCore");
const GradeChapter = require("../../../shared/GradeChapter");
const OpsBlock = require("./OpsBlock");
const ResultUtil = require("./util/ResultUtil");
const {
	atBlock,
	atChapter,
	atChapters,
	atUser,
	atMiscBlock,
} = require("./StateCursors");
const {
	PENCIL_LINE_WIDTHS,
	DEFAULT_PENCIL_LINE_WIDTH,
	DEFAULT_SOUNDS_VOLUME,
	DEFAULT_THEATER_VOLUME,
	DEFAULT_THEATER_PLAYBACK_RATE,
	ACADEMY_PURCHASE_ID,
	VIRTUAL_CAMPUS_SCHOOL_ID,
} = require("../../../shared/Constants");
const {
	DRAWVAS_COLOR_OPTIONS,
	DRAWVAS_COLOR_DEFAULT,
} = require("../util/ColorUtil");
const {getFeatureFlag} = require("../util/FeatureFlags");

// TODO: Do we even have all the fields? any registration things go here?
/*
User shape {
  load,
  loadError,
  userID,
  imitatorUserID,
	imitatorIdentifier,
	imitatorRoles,
  model,
  requestedPage: An object with page, if a page has been requested and requestedAt, the time it was requested
  logoutPage: The page to go to after logout
  currentActivity,
  possibleUsernames,
  loginError,
  sessionExpired: a boolean indicating if user's socket session is no longer valid
	notLoggedIn: a boolean indicating for sure the user is not logged in
  authenticated,
  isLoadingUsernames,
  isAcceptingPrivacy,
  submittingBugReport,
}
*/

const init = function() {
	return LoadState.loadInit({
		userID: null,
		imitatorUserID: null,
		imitatorIdentifier: null,
		imitatorRoles: null,
		model: null,
		requestedPage: null,
		logoutPage: null,
		currentActivity: null,
		loginUsername: null,
		possibleUsernames: null,
		loginError: null,
		invalidToken: null,
		sessionExpired: false,
		registrationCode: null,
		authenticated: false,
		notLoggedIn: null,
		isLoadingUsernames: false,
		isAcceptingPrivacy: false,
		submittingBugReport: false,
		placementTest: {load: "none"},
	});
};

const loadStart = function(user, api) {
	return LoadState.loadStart(user, () => api.loadUser());
};

const loadRestart = function(user, api) {
	user = LoadState.loadRestart(user);
	return LoadState.loadStart(user, () => api.loadUser());
};

// Used to make the whole app go into a "loading" state prior to navigating externally.
// Used for navigating to placement test.
const loadPending = function(user) {
	return LoadState.loadPending(user);
};

const addXP = function(user, earnedXP) {
	if (!user.model) {
		return user;
	}
	return setIn(user, ["model", "xp"], oldXP => oldXP + earnedXP);
};

const addBucks = function(user, addedBucks) {
	if (!user.model) {
		return user;
	}
	return setIn(user, ["model", "bucks"], oldBucks => oldBucks + addedBucks);
};

const recomputeOptHWOnCompleteBlock = function(user, blockID, stars) {
	const homework = user.model.homework;
	const optionalHWGroups = homework.optionalHWGroups;

	const nextHWGroups = [];
	let nextOptionalBlockIDs = [];

	_.each(optionalHWGroups, hwGroup => {
		let nextGroup = hwGroup;

		if (_.has(hwGroup.blockStars, blockID)) {
			nextGroup = setIn(hwGroup, ["blockStars", blockID], stars);
		}

		const numOptCompleted = _.filter(_.map(nextGroup.blockStars, s => s > 0))
			.length;

		if (numOptCompleted < nextGroup.numOptionalBlocks) {
			let optIDs = _.keys(nextGroup.blockStars);
			optIDs = _.map(optIDs, x => parseInt(x, 10));

			nextOptionalBlockIDs = nextOptionalBlockIDs.concat(optIDs);
			nextHWGroups.push(nextGroup);
		}
	});

	return _.extend({}, homework, {
		optionalHWGroups: nextHWGroups,
		optionalBlockIDs: nextOptionalBlockIDs,
	});
};

// Given a response to a students slot endpoint, set the students on the user
// model.
const setStudentsFromSlot = function(user, data) {
	if (!user || !user.model) return user;
	// This method is called for slots that only load details of one student
	// after loading all students previously, so some careful merging required
	// here.
	const oldStudents = _.get(user, "model.students", []);
	const studentsByID = _.merge(_.keyBy(oldStudents, "id"), data.students);
	const students = _.map(studentsByID, (s, id) =>
		_.extend(
			{id: parseInt(id)},
			_.pick(s, [
				"avatarData",
				"displayName",
				"hasRegistered",
				"hasCompletedRegistration",
				"isActive",
				"username",
				"inActiveClasses",
				"hasAcademyPurchase",
				"everHadBAOAccess",
				"everHadBAOSubscription",
			])
		)
	);
	return setIn(user, ["model", "students"], _.sortBy(students, "displayName"));
};

// Is definitely logged in
const isLoggedIn = function(user) {
	return user.authenticated && user.load === "done";
};

// User does not exist in the BA database. Users are logged in users
// from Platform that do not have accounts on BA. All platform guests
// are uninitialized.
const isPlatformGuest = function(user) {
	return isLoggedIn(user) && !user.userID && user.platformUserID;
};

// Is the user initialized on BA? User can be in the BA database but
// still be not initialized on BA (like invited BA teachers that are
// already Platform users that haven't accepted invite). Initialized
// users include:
// - Users created directly on BA that didn't previously exist on
//   Platform.
// - Users that buy subscriptions via checkout or PO.
// - Users that redeem subscriptions.
// - Users that "accept" PO subscription invites.
// - Users that "accept" teacher invites.
// - Users that are initialized are "ready" to use BA to the full
//   extent of their role.
const isInitialized = function(user) {
	return _.get(user, "model.isInitialized");
};

// Is a platform user and is restricted OR no email
const isRestrictedPlatformUser = function(user) {
	return (
		user.load === "done" &&
		!!user.platformUserID &&
		(user.isRestricted || !user.model.email)
	);
};

const isIplUser = function(user) {
	return user.load === "done" && !!user.isIplUser;
};

const IPL_STAFF_EMAIL_DOMAIN = "@inflectionpointlearning.com";

const hasIplStaffEmail = function(email) {
	return (
		!!email &&
		String(email)
			.toLowerCase()
			.endsWith(IPL_STAFF_EMAIL_DOMAIN)
	);
};

const isIplStaff = function(user) {
	if (!user || user.load !== "done") {
		return false;
	}
	if (hasIplStaffEmail(getEmail(user))) {
		return true;
	}
	return hasIplStaffEmail(_.get(user, "imitatorIdentifier"));
};

// notLoggedIn only set when authentication fails
const isDefinitelyNotLoggedIn = function(user) {
	return user.notLoggedIn;
};

const hasAcceptedTerms = function(user) {
	return !!user.model.lastAcceptedPrivacyAt;
};

const getUserRoles = function(user) {
	return _.get(user, "model.roles", []);
};

const getUserName = function(user, page) {
	return page.demo ? "Guest" : user.model && user.model.displayName;
};

const getUserGrade = function(user) {
	return _.get(user, "model.gradeID", 3);
};

const getEmail = function(user) {
	return _.get(user, "model.email", "");
};

const hasKey = function(user, key) {
	const userPerms = _.get(user, "model.perms", {});
	return _.find(userPerms, (val, kn) => _.startsWith(kn, key));
};

const hasKeyForRef = function(user, key, ref) {
	const userPerms = _.get(user, "model.perms", {});
	return _.find(userPerms, (val, kn) => {
		const keyParts = kn.split("#");
		return keyParts[0] === key && keyParts[1] === ref.toString();
	});
};

const hasRole = function(user, role) {
	const userRoles = getUserRoles(user);
	return _.includes(userRoles, role);
};

const hasParentRole = function(user) {
	return hasRole(user, "parent");
};

// Checks if the user has only a student persona on Platform.
const isStudentOnly = function(user) {
	return (
		_.get(user, "model.identifiesAsStudent", false) &&
		!_.get(user, "model.identifiesAsParent", false) &&
		!_.get(user, "model.identifiesAsEducator", false)
	);
};

const hasStudentRole = function(user) {
	return hasRole(user, "student") || hasRole(user, "student deactivated");
};

const hasTeacherRole = function(user) {
	return hasRole(user, "teacher") || hasRole(user, "teacher deactivated");
};

const hasAoPSRole = function(user) {
	return hasRole(user, "aops");
};

const hasOpsAcademyRole = function(user) {
	return hasRole(user, "ops academy");
};

const isBetaParent = function(user) {
	return hasRole(user, "parent beta");
};

const isStudent = function(user) {
	return user && isLoggedIn(user) && hasStudentRole(user);
};

// Should use this function to determine if we can use Analytics for this user.
const isNotStudentOrNotLoggedIn = function(user) {
	return isDefinitelyNotLoggedIn(user) || isNotStudent(user);
};

// Returns true if user is an active student in a school.
const isSchoolStudent = function(user) {
	const schoolIDs = _.get(user, ["model", "schoolIDs"]);
	const isActiveInSchool = schoolIDs && schoolIDs.length > 0;
	return !!_.get(user, "model.purchaseInfo.length") && isActiveInSchool;
};

// Home (non–school-roster) student.
// Do not use purchaseInfo.length as the signal — it only reflects
// rows from school_purchases / student relations; family BA Online access may not.
const isFamilySubscriptionStudent = function(user) {
	return isStudent(user) && !isSchoolStudent(user);
};

const isVCStudent = function(user) {
	const studentSchoolID = _.get(user, "model.homework.forSchool", 0);
	return studentSchoolID === VIRTUAL_CAMPUS_SCHOOL_ID;
};

const isAcademyStudent = function(user) {
	return isStudent(user) && isAcademyUser(user);
};

const getStudentDomain = function(user) {
	return _.get(user, "model.domain");
};

const getAdultTypeForStudent = function(user) {
	const inSchool = isSchoolStudent(user);
	return inSchool ? "teacher" : "parent";
};

// Not straight inverse of isStudent; both return false when we don't know.
const isNotStudent = function(user) {
	return user && isLoggedIn(user) && !hasStudentRole(user);
};

const hasTrainerPermission = function(user) {
	// User must be initialized and have trainer permisssions
	return (
		_.get(user, "model.hasTrainerPermission", false) &&
		_.get(user, "model.isInitialized", false)
	);
};

/**
 * isPurchaseAdmin, isSchoolAdmin, and isTeacher should only be used
 * if we don't have access to schoolState, otherwise
 * functions in OpsSchool should be used
 */

const isPurchaseAdmin = function(user) {
	const staffRoles = _.get(user, "model.staffRoles", {});
	return staffRoles.adminPurchase;
};

const isSchoolAdmin = function(user) {
	const staffRoles = _.get(user, "model.staffRoles", {});
	return !staffRoles.adminPurchase && _.size(staffRoles.adminSchoolIDs);
};

const isTeacher = function(user) {
	const staffRoles = _.get(user, "model.staffRoles", {});
	return !staffRoles.adminPurchase && _.size(staffRoles.adminSchoolIDs) === 0;
};

const isAcademyUser = function(user) {
	const academyPurchase =
		_.get(user, ["model", "purchaseInfo", 0, "purchaseID"]) ===
		ACADEMY_PURCHASE_ID;
	const schoolIDs = _.get(user, ["model", "schoolIDs"]);
	const activeInSchool = schoolIDs && schoolIDs.length > 0;
	return academyPurchase && activeInSchool;
};

const isAcademyStaff = function(user) {
	return getStaffedPurchaseID(user) === ACADEMY_PURCHASE_ID;
};

// Whether the user is a student or teacher at any school IDs in a given array
const isUserInSchools = function(user, schoolIDs) {
	const staffSchools = _.get(user, ["model", "staffSchools"], {});
	const staffSchoolIDs = _.map(_.keys(staffSchools), i => parseInt(i));
	const studentSchoolIDs = _.get(user, ["model", "schoolIDs"], []);
	return (
		_.intersection(staffSchoolIDs, schoolIDs).length ||
		_.intersection(studentSchoolIDs, schoolIDs).length
	);
};

// These are the school tools pages
// If a purchase is expired, all the teachers have "teacher deactivated role"
// We want purchase admins/school admins/teachers to still have access to the dashboard even if they can't do any actions.
// We also want teachers who aren't assigned to any classes to be able to have access to the dashboard
const hasDashboard = function(user) {
	return !!getStaffedPurchaseID(user);
};

const hasRosteringTab = function(user) {
	const staffRoles = _.get(user, ["model", "staffRoles"], {});
	const forPurchaseID = _.get(user, ["model", "staffForPurchaseID"]);
	return staffRoles.adminPurchase && forPurchaseID !== ACADEMY_PURCHASE_ID;
};

const getDefaultMostRecentPageData = PageShared.getDefaultMostRecentPageData;

// Unlike user.model.mostRecentPageData, fills in suitable defaults.
const getMostRecentPageData = PageShared.getMostRecentPageDataForUser;

const requestPage = function(user, page, clock) {
	return setIn(user, "requestedPage", {
		page,
		requestedAt: clock.now(),
	});
};

const isStaffActive = function(user) {
	return _.get(user, "model.staffRoles.active") && hasRole(user, "teacher");
};

const updateLastPlayedData = function(user, result) {
	// ignore last played data on BAEditor, since we don't even have a user
	if (global.IS_ON_BAEDITOR) return user;

	if (user) {
		const {source} = result;
		return setIn(user, "model.lastPlayedData", lpd => {
			if (!lpd) lpd = {};
			if (source.type === "lab") {
				return TrainerCore.updateLastPlayedData(lpd, {
					typeID: source.typeID,
				});
			} else {
				const lastPlayedData = {
					gradeNumber: source.gradeNumber,
					chapterID: GradeChapter.toChapterID(
						source.gradeNumber,
						source.chapterIndex
					),
					testInProgress: lpd.testInProgress,
				};
				// book tests shouldn't define avatar placement since they have no room
				if (result.resultType !== "bookTest") {
					_.extend(lastPlayedData, {
						room: {
							blockID: source.blockID,
							isTrophy:
								source.type === "trophy" || source.type === "demoTrophy",
							completed: ResultUtil.isSuccessfullyComplete(result),
						},
					});
				}
				return TrainerCore.updateLastPlayedData(lpd, lastPlayedData);
			}
		});
	}
	return user;
};

// similar to updateLastPlayedData, but accepts a room instead of a result
const updateLastPlayedDataWithRoom = (user, room) => {
	if (!_.isObject(room)) return user;

	if (user) {
		room = _.pick(room, [
			"chapterID",
			"completed",
			"isReading",
			"isTrophy",
			"blockID",
			"bookSectionID",
		]);
		// update with room
		setIn(user, "model.lastPlayedData", lpd => {
			const lastPlayedData = {room};
			return TrainerCore.updateLastPlayedData(lpd, lastPlayedData);
		});
	}
	return user;
};

const getTestInProgressInfo = function(user) {
	const testData = _.get(user, "model.lastPlayedData.testInProgress");
	if (testData) {
		if (testData.chapterID) {
			const {chapterID} = testData;
			return GradeChapter.chapterIDToContext(chapterID) || {};
		} else if (testData.miscBlockName) {
			return {miscBlockName: testData.miscBlockName};
		} else {
			// Keeping this fallback return for old chapter tests where the testData
			// was the chapterID directly for compatibility.
			return testData;
		}
	} else {
		return null;
	}
};

const getTestInProgressBlock = function(user, state) {
	const testData = _.get(user, "model.lastPlayedData.testInProgress");
	if (testData) {
		if (testData.miscBlockName) {
			return atMiscBlock(state, testData.miscBlockName).get();
		} else {
			const blockID =
				_.get(testData, "blockID") ||
				atChapter(state, testData).getIn(["model", "testBlockID"]);
			return atBlock(state, blockID).get();
		}
	} else {
		return null;
	}
};

const clearTestInProgress = function(state) {
	return atUser(state).setIn("model.lastPlayedData.testInProgress", null);
};

const setTestInProgress = function(state, block) {
	const blockID = _.get(block, "blockID");
	let testData;
	const bookTestHash = OpsBlock.getBookTestHash(block);
	if (bookTestHash) {
		testData = {miscBlockName: "bookTest" + bookTestHash};
	} else {
		const chapterID = _.get(block, "model.chapterID");
		testData = blockID && chapterID && {blockID, chapterID};
	}
	return atUser(state).setIn(
		["model", "lastPlayedData", "testInProgress"],
		testData
	);
};

// Returns true when the student has completed at least one chapter assessment:
// some trainer_chapters.testBlockID whose block has timesCompleted > 0 (from
// trainer_studentBlocks on the server; block model on the client).
const hasTakenAnyChapterTest = function(state) {
	const chaptersMap = atChapters(state).get();
	if (!chaptersMap || !_.isPlainObject(chaptersMap)) {
		return false;
	}
	return _.some(chaptersMap, chapterEntry => {
		const testBlockID = _.get(chapterEntry, "model.testBlockID");
		if (!testBlockID) {
			return false;
		}
		const timesCompleted = atBlock(state, testBlockID).getIn([
			"model",
			"timesCompleted",
		]);
		return (timesCompleted || 0) > 0;
	});
};

const bookTestAssignedNow = function(user) {
	const bookTestData = _.get(user, "model.homework.bookTestInfo");
	if (bookTestData) {
		return _.find(bookTestData, btd => {
			if (!btd.testStartsAt) return false;
			return (
				moment(btd.testStartsAt)
					.subtract(1, "hours")
					.isBefore(moment()) &&
				moment().isBefore(moment(btd.testStartsAt).add(3, "hours"))
			);
		});
	} else {
		return false;
	}
};

/**
 * Retrieves a list of unused account-setup codes for the given user.
 *
 * @param {*} user user object from the vault state
 * @returns an array of codes that have not been used
 */
const getUnacceptedTeacherInvites = function(user) {
	return _.get(user, "model.unacceptedTeacherInvites", []);
};

const getOneOffRewards = function(user) {
	return _.get(user, "model.oneOffRewards", []);
};

/* function returns user model lastPlayedData or constructs it if not complete*/
const getLastPlayedDataFromUser = PageShared.getLastPlayedDataFromUser;

const updateSequenceData = function(user, page) {
	if (!_.get(user, "model")) return user;

	const lastPlayedData = getLastPlayedDataFromUser(user);
	const justLoggedIn = _.get(user, "justLoggedIn");

	//check for initial log in. We reset to lastPlayedData in this case
	if (justLoggedIn) {
		user = extendIn(user, "model.sequenceData", lastPlayedData);
	}

	// update if page is playable trainer/library/theater and values are new
	const newPageData = _.pick(page, ["gradeNumber", "chapterIndex"]);
	if (PageShared.isCoreTrainerPage(page)) {
		return extendIn(user, "model.sequenceData", newPageData);
	}

	return user;
};

const getSequenceDataFromUser = function(user) {
	return _.get(user, "model.sequenceData", {});
};

const getStaffedPurchaseID = function(user) {
	return _.get(user, "model.staffForPurchaseID", null);
};

const areSoundsEnabled = function(user) {
	const isEnabled = _.get(user, "model.settings.soundsEnabled");
	return _.isUndefined(isEnabled) || isEnabled;
};

const isMusicEnabled = function(user) {
	const isEnabled = _.get(user, "model.settings.musicEnabled");
	return _.isUndefined(isEnabled) || isEnabled;
};

const getMusicVolume = function(user) {
	const volume = _.get(user, "model.settings.musicVolume");
	return _.isNumber(volume) ? volume : DEFAULT_SOUNDS_VOLUME;
};

const getSoundsVolume = function(user) {
	const volume = _.get(user, "model.settings.soundsVolume");
	return _.isNumber(volume) ? volume : DEFAULT_SOUNDS_VOLUME;
};

const getPencilColor = function(user) {
	const c = _.get(user, "model.settings.pencilColor", DRAWVAS_COLOR_DEFAULT);
	return _.includes(DRAWVAS_COLOR_OPTIONS, c) ? c : DRAWVAS_COLOR_DEFAULT;
};

const getPencilLineWidth = function(user) {
	const lw = _.get(
		user,
		"model.settings.pencilLineWidth",
		DEFAULT_PENCIL_LINE_WIDTH
	);
	return _.includes(PENCIL_LINE_WIDTHS, lw) ? lw : DEFAULT_PENCIL_LINE_WIDTH;
};

const getTheaterVolume = function(user) {
	const volume = _.get(user, "model.settings.theaterVolume");
	return _.isNumber(volume) ? volume : DEFAULT_THEATER_VOLUME;
};

const getTheaterPlaybackRate = function(user) {
	const speed = _.get(user, "model.settings.theaterPlaybackRate");
	return _.isNumber(speed) ? speed : DEFAULT_THEATER_PLAYBACK_RATE;
};

const getTheaterSoundEnabled = function(user) {
	const isEnabled = _.get(user, "model.settings.theaterSoundEnabled");
	return _.isUndefined(isEnabled) || isEnabled;
};

const getTheaterCaptionsOn = function(user) {
	const isEnabled = _.get(user, "model.settings.theaterCaptionsOn");
	return !_.isUndefined(isEnabled) && isEnabled;
};

const getMiscellaneousUserData = function(user) {
	return _.get(user, "model.miscellaneous", {});
};

//TODO: When needed, we should add a function that gets and sets certain fields in model.miscellaneous with
// appropriate defaults (where applicable).

const getSuppressManualLockShowConfirm = function(user) {
	const isSuppressed = _.get(
		user,
		"model.miscellaneous.suppressManualLockShowConfirm"
	);
	return !_.isUndefined(isSuppressed) && isSuppressed;
};

const setSuppressManualLockShowConfirm = function(user, suppress = true) {
	if (!user || !user.model) return user;
	return setIn(
		user,
		["model", "miscellaneous", "suppressManualLockShowConfirm"],
		suppress
	);
};

const setUserJustLoggedIn = function(user, justLoggedIn) {
	if (!user) return user;
	return setIn(user, ["justLoggedIn"], justLoggedIn);
};

// Returns true if you are a parent with active student subscriptions, an active Academy class,
// or you are an active teacher of an active school purchase
const canViewReportProblems = function(user) {
	let isActive = true;
	if (_.get(user, "model.staffRoles.active")) {
		if (!_.get(user, "model.staffRoles.purchaseActive")) {
			isActive = false;
		}
	} else if (_.get(user, "model.students", []).length) {
		if (
			!_.some(_.get(user, "model.students"), s => s.isActive) &&
			!_.some(_.get(user, "model.students"), s => s.inActiveClasses)
		) {
			isActive = false;
		}
	}

	return isActive;
};

const canAccessBeastbuilder = function(user) {
	const hasTrainerPerms = getIn(user, ["model", "hasTrainerPermission"]);
	if (!hasTrainerPerms) return false;
	const {beastbuilderProgressUnlocked, beastbuilderLockOverride} = _.get(
		user,
		"model",
		{}
	);
	return beastbuilderProgressUnlocked && beastbuilderLockOverride !== "locked";
};

// Determines whether leaderboards appear in trainer or whether they are completely hidden.
const canViewLeaderboards = function(user) {
	const isHQ = hasAoPSRole(user) || hasOpsAcademyRole(user);

	// Check if academy leaderboards are enabled via feature flag
	const academyLeaderboardsEnabled = getFeatureFlag(
		"ENABLE_ACADEMY_LEADERBOARDS",
		false
	);

	// Only family students (non-school students) and HQ can view leaderboards
	// Academy students can view if the feature flag is enabled
	const isAllowedStudent =
		isStudent(user) &&
		(!isSchoolStudent(user) ||
			(isAcademyUser(user) && academyLeaderboardsEnabled));
	return isHQ || isAllowedStudent;
};

// Determines whether leaderboards are locked to this user. The leaderboards icon will
// still be visible even if leaderboards are locked.
const canAccessLeaderboards = function(user) {
	const hasTrainerPerms = getIn(user, ["model", "hasTrainerPermission"]);
	if (!hasTrainerPerms) return false;
	const {leaderboardsLockOverride} = _.get(user, "model", {});
	return leaderboardsLockOverride !== "locked";
};

const canReportBugs = function(user) {
	if (hasRole(user, "student coppa")) return false;
	if (hasKey(user, "student disabled bug reports")) return false;
	return true;
};

/**
 * Assuming the passed in blockModel is assigned as homework, this function
 * calculates some basic information about if it's an academy homework (and if
 * it is, whether or not it's for the current week), and whether or not the
 * homework is completed (i.e. has stars)
 *
 * @param   {Object}  user        The user object directly from the vault
 * @param   {Object}  blockModel  block model of the block in question
 *
 * @return  {Object}              {isAcademy, isCurrentWeek, isComplete}
 */
const getHomeworkDataForBlock = function(user, blockModel) {
	const {blockID} = blockModel;
	const currentHomework = _.get(user, "model.homework.currentBlockIDs");
	const isAcademy = isAcademyUser(user);
	return {
		isAcademy,
		isCurrentWeek: isAcademy && _.includes(currentHomework, blockID),
		isComplete: OpsBlock.isBlockComplete(blockModel),
	};
};

const getCustomScopeForUser = function(user) {
	return _.get(user, "model.customScope", []);
};

const getStudentOnboardingProgress = function(user) {
	return _.get(user, "model.eventsTriggered.tutorials.studentOnboardingV2");
};

const shouldSeeStudentOnboarding = function(user) {
	const studentOnboardingProgress = getStudentOnboardingProgress(user);
	const hasStudentOnboardingProgress = !!studentOnboardingProgress;
	const currentPart = studentOnboardingProgress?.currentPart;
	const currentStep = studentOnboardingProgress?.currentStep;
	const isCompleted =
		currentPart === "completed" && currentStep === "completed";
	return hasStudentOnboardingProgress && !isCompleted;
};

const hasCompletedStudentOnboarding = function(user) {
	const studentOnboardingProgress = getStudentOnboardingProgress(user);
	const currentPart = studentOnboardingProgress?.currentPart;
	const currentStep = studentOnboardingProgress?.currentStep;
	return currentPart === "completed" && currentStep === "completed";
};

module.exports = {
	init,
	loadStart,
	loadRestart,
	loadPending,
	loadSuccess: LoadState.loadSuccess,
	loadError: LoadState.loadError,
	addXP,
	addBucks,
	setStudentsFromSlot,
	recomputeOptHWOnCompleteBlock,
	isLoggedIn,
	isPlatformGuest,
	isInitialized,
	isRestrictedPlatformUser,
	isIplUser,
	isIplStaff,
	hasIplStaffEmail,
	isDefinitelyNotLoggedIn,
	getUserRoles,
	getUserName,
	getUserGrade,
	getEmail,
	isStudentOnly,
	hasStudentRole,
	hasParentRole,
	hasTeacherRole,
	hasAoPSRole,
	hasOpsAcademyRole,
	hasRosteringTab,
	isBetaParent,
	isStaffActive,
	hasKey,
	hasKeyForRef,
	hasRole,
	isNotStudentOrNotLoggedIn,
	isSchoolAdmin,
	isPurchaseAdmin,
	isTeacher,
	isAcademyUser,
	isAcademyStaff,
	isAcademyStudent,
	isUserInSchools,
	hasDashboard,
	isStudent,
	isFamilySubscriptionStudent,
	isSchoolStudent,
	isVCStudent,
	getStudentDomain,
	getAdultTypeForStudent,
	isNotStudent,
	hasTrainerPermission,
	getMostRecentPageData,
	getDefaultMostRecentPageData,
	requestPage,
	updateLastPlayedData,
	updateLastPlayedDataWithRoom,
	getTestInProgressInfo,
	getTestInProgressBlock,
	clearTestInProgress,
	setTestInProgress,
	hasTakenAnyChapterTest,
	getStaffedPurchaseID,
	getLastPlayedDataFromUser,
	bookTestAssignedNow,
	updateSequenceData,
	getSequenceDataFromUser,
	areSoundsEnabled,
	isMusicEnabled,
	getMusicVolume,
	getSoundsVolume,
	getPencilColor,
	getPencilLineWidth,
	getTheaterVolume,
	getTheaterPlaybackRate,
	getTheaterSoundEnabled,
	getTheaterCaptionsOn,
	getMiscellaneousUserData,
	getSuppressManualLockShowConfirm,
	setSuppressManualLockShowConfirm,
	setUserJustLoggedIn,
	canViewReportProblems,
	canAccessBeastbuilder,
	canViewLeaderboards,
	canAccessLeaderboards,
	canReportBugs,
	getHomeworkDataForBlock,
	getUnacceptedTeacherInvites,
	getOneOffRewards,
	getCustomScopeForUser,
	hasAcceptedTerms,
	getStudentOnboardingProgress,
	shouldSeeStudentOnboarding,
	hasCompletedStudentOnboarding,
};
